// src/components/Contact.js
import React, { useState } from "react";
import styled from "styled-components";
import {
  FaEnvelope,
  FaGlobe,
  FaPaperPlane,
  FaClock,
} from "react-icons/fa";
import theme from "../styles/theme";

const ContactSection = styled.section`
  padding: ${theme.spacing[24]} ${theme.spacing[4]};
  background: linear-gradient(135deg, ${theme.secondary[50]} 0%, ${theme.secondary[100]} 100%);
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing[6]};
  
  @media (max-width: ${theme.breakpoints.sm}) {
    padding: 0 ${theme.spacing[4]};
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing[16]};
`;

const SectionTitle = styled.h2`
  font-size: clamp(${theme.typography.fontSize['2xl']}, 4vw, ${theme.typography.fontSize['4xl']});
  color: ${theme.secondary[900]};
  margin-bottom: ${theme.spacing[4]};
  background: ${theme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: ${theme.typography.fontWeight.bold};
`;

const SectionSubtitle = styled.p`
  font-size: ${theme.typography.fontSize.xl};
  color: ${theme.secondary[600]};
  max-width: 700px;
  margin: 0 auto;
  line-height: ${theme.typography.lineHeight.relaxed};
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[16]};
  align-items: start;

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing[8]};
  }
`;

const ContactInfo = styled.div`
  background: white;
  border-radius: ${theme.borderRadius['2xl']};
  padding: ${theme.spacing[12]};
  box-shadow: ${theme.shadows.lg};
  border: 1px solid ${theme.secondary[200]};
`;

const ContactInfoTitle = styled.h3`
  font-size: ${theme.typography.fontSize['2xl']};
  color: ${theme.secondary[900]};
  margin-bottom: ${theme.spacing[8]};
  position: relative;
  font-weight: ${theme.typography.fontWeight.semibold};

  &::after {
    content: "";
    position: absolute;
    bottom: -${theme.spacing[2]};
    left: 0;
    width: 60px;
    height: 3px;
    background: ${theme.gradients.primary};
    border-radius: ${theme.borderRadius.base};
  }
`;

const ContactInfoText = styled.p`
  font-size: ${theme.typography.fontSize.lg};
  color: ${theme.secondary[600]};
  line-height: ${theme.typography.lineHeight.relaxed};
  margin-bottom: ${theme.spacing[8]};
`;

const ContactMethods = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const ContactMethod = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  padding: ${theme.spacing[4]};
  background: ${theme.secondary[50]};
  border-radius: ${theme.borderRadius.xl};
  transition: all ${theme.animation.normal} ease;

  &:hover {
    background: ${theme.secondary[100]};
    transform: translateX(5px);
  }
`;

const ContactIcon = styled.div`
  width: 60px;
  height: 60px;
  background: ${theme.gradients.accent};
  border-radius: ${theme.borderRadius.xl};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: ${theme.typography.fontSize.xl};
  flex-shrink: 0;
`;

const ContactDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
`;

const ContactLabel = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.secondary[500]};
  font-weight: ${theme.typography.fontWeight.medium};
`;

const ContactValue = styled.div`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.secondary[900]};
  font-weight: ${theme.typography.fontWeight.semibold};
`;

const ContactForm = styled.form`
  background: white;
  border-radius: ${theme.borderRadius['2xl']};
  padding: ${theme.spacing[12]};
  box-shadow: ${theme.shadows.lg};
  border: 1px solid ${theme.secondary[200]};
`;

const FormTitle = styled.h3`
  font-size: ${theme.typography.fontSize['2xl']};
  color: ${theme.secondary[900]};
  margin-bottom: ${theme.spacing[8]};
  position: relative;
  font-weight: ${theme.typography.fontWeight.semibold};

  &::after {
    content: "";
    position: absolute;
    bottom: -${theme.spacing[2]};
    left: 0;
    width: 60px;
    height: 3px;
    background: ${theme.gradients.primary};
    border-radius: ${theme.borderRadius.base};
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[6]};
  margin-bottom: ${theme.spacing[6]};

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing[4]};
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[6]};
`;

const FormLabel = styled.label`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.secondary[700]};
  font-weight: ${theme.typography.fontWeight.medium};
`;

const FormInput = styled.input`
  padding: ${theme.spacing[4]};
  border: 2px solid ${theme.secondary[200]};
  border-radius: ${theme.borderRadius.xl};
  font-size: ${theme.typography.fontSize.base};
  transition: all ${theme.animation.normal} ease;
  background: ${theme.secondary[50]};

  &:focus {
    outline: none;
    border-color: ${theme.primary[500]};
    background: white;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: ${theme.secondary[400]};
  }
`;

const FormTextarea = styled.textarea`
  padding: ${theme.spacing[4]};
  border: 2px solid ${theme.secondary[200]};
  border-radius: ${theme.borderRadius.xl};
  font-size: ${theme.typography.fontSize.base};
  min-height: 120px;
  resize: vertical;
  transition: all ${theme.animation.normal} ease;
  background: ${theme.secondary[50]};
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${theme.primary[500]};
    background: white;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: ${theme.secondary[400]};
  }
`;

const SubmitButton = styled.button`
  background: ${theme.gradients.accent};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.full};
  padding: ${theme.spacing[4]} ${theme.spacing[8]};
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: all ${theme.animation.normal} ease;
  box-shadow: 0 4px 14px rgba(16, 185, 129, 0.3);
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  width: fit-content;
  margin-top: ${theme.spacing[8]};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(16, 185, 129, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    transition: transform ${theme.animation.normal} ease;
  }

  &:hover:not(:disabled) svg {
    transform: translateX(2px);
  }
`;

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Create mailto link with form data
      const subject = encodeURIComponent(`ISO Audit Pro Inquiry from ${formData.firstName} ${formData.lastName}`);
      const body = encodeURIComponent(
        `Name: ${formData.firstName} ${formData.lastName}\n` +
        `Email: ${formData.email}\n` +
        `Company: ${formData.company}\n\n` +
        `Message:\n${formData.message}\n\n` +
        `---\nThis inquiry was sent from the ISO Audit Pro website.`
      );
      const mailtoLink = `mailto:sg@isoauditpro.com?subject=${subject}&body=${body}`;
      
      // Open email client
      window.location.href = mailtoLink;
      
      // Reset form after a short delay
      setTimeout(() => {
        setIsSubmitting(false);
        alert("Thank you for your message! Your email client should open shortly. We'll get back to you soon.");
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          company: "",
          message: "",
        });
      }, 1000);
    } catch (error) {
      setIsSubmitting(false);
      alert("There was an issue sending your message. Please try again or contact us directly at sg@isoauditpro.com");
    }
  };

  return (
    <ContactSection id="contact">
      <Container>
        <SectionHeader>
          <SectionTitle>Start Your Accreditation Journey</SectionTitle>
          <SectionSubtitle>
            Ready to achieve ISO 17025, ISO 17020, or AISC compliance? 
            Connect with our international experts today for personalized consultation 
            and comprehensive accreditation support.
          </SectionSubtitle>
        </SectionHeader>

        <ContactGrid>
          <ContactInfo>
            <ContactInfoTitle>Connect with Global Experts</ContactInfoTitle>
            <ContactInfoText>
              Our international team of ISO 17025, ISO 17020, and AISC specialists 
              is ready to support your accreditation journey. Get personalized 
              consultation for your compliance needs worldwide.
            </ContactInfoText>

            <ContactMethods>
              <ContactMethod>
                <ContactIcon>
                  <FaEnvelope />
                </ContactIcon>
                <ContactDetails>
                  <ContactLabel>Email</ContactLabel>
                  <ContactValue>sg@isoauditpro.com</ContactValue>
                </ContactDetails>
              </ContactMethod>

              <ContactMethod>
                <ContactIcon>
                  <FaGlobe />
                </ContactIcon>
                <ContactDetails>
                  <ContactLabel>Global Reach</ContactLabel>
                  <ContactValue>Serving Clients Worldwide</ContactValue>
                </ContactDetails>
              </ContactMethod>

              <ContactMethod>
                <ContactIcon>
                  <FaClock />
                </ContactIcon>
                <ContactDetails>
                  <ContactLabel>Response Time</ContactLabel>
                  <ContactValue>
                    24-48 Hours
                    <br />
                    International Support Available
                  </ContactValue>
                </ContactDetails>
              </ContactMethod>
            </ContactMethods>
          </ContactInfo>

          <ContactForm onSubmit={handleSubmit}>
            <FormTitle>Send Us a Message</FormTitle>

            <FormGrid>
              <FormGroup style={{ marginBottom: 0 }}>
                <FormLabel htmlFor="firstName">First Name *</FormLabel>
                <FormInput
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your first name"
                />
              </FormGroup>

              <FormGroup style={{ marginBottom: 0 }}>
                <FormLabel htmlFor="lastName">Last Name *</FormLabel>
                <FormInput
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your last name"
                />
              </FormGroup>
            </FormGrid>

            <FormGroup>
              <FormLabel htmlFor="email">Email Address *</FormLabel>
              <FormInput
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="Enter your email address"
              />
            </FormGroup>

            <FormGroup>
              <FormLabel htmlFor="company">Company</FormLabel>
              <FormInput
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                placeholder="Enter your company name"
              />
            </FormGroup>

            <FormGroup style={{ marginBottom: 0 }}>
              <FormLabel htmlFor="message">Message *</FormLabel>
              <FormTextarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                placeholder="Tell us about your organization, current compliance status, and how we can help with your accreditation needs..."
              />
            </FormGroup>

            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Sending..." : "Send Message"}
              <FaPaperPlane />
            </SubmitButton>
          </ContactForm>
        </ContactGrid>
      </Container>
    </ContactSection>
  );
};

export default Contact;
